import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import {
  Type, Square, Circle, Triangle, ImageIcon, Video, Music,
  Minus as LineIcon, Trash2, Copy, Bold, Italic, Underline,
  Strikethrough, AlignLeft, AlignCenter, AlignRight,
  Undo2, Redo2, Eye, Save, X, ArrowUp, ArrowDown,
  Play, ImageDown, List, ListOrdered, IndentIncrease, IndentDecrease, MonitorPlay,
  Table2, EyeOff, Plus, RowsIcon, Columns2, Trash
} from 'lucide-react';
import { TableGridSelector } from './TableGridSelector';

const FONTS = [
  'Arial', 'Arial Black', 'Georgia', 'Times New Roman', 'Courier New',
  'Verdana', 'Trebuchet MS', 'Tahoma', 'Impact', 'Comic Sans MS',
  'Palatino Linotype', 'Lucida Console', 'Garamond', 'Book Antiqua',
];
const FONT_SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96, 128];

const TABS = [
  { id: 'home', label: 'Home' },
  { id: 'insert', label: 'Insert' },
  { id: 'design', label: 'Design' },
  { id: 'transition', label: 'Transition' },
  { id: 'animation', label: 'Animation' },
];

const TRANSITION_TYPES = [
  { id: 'none', label: 'None' },
  { id: 'fade', label: 'Fade' },
  { id: 'slide-left', label: 'Slide Left' },
  { id: 'slide-right', label: 'Slide Right' },
  { id: 'slide-up', label: 'Slide Up' },
  { id: 'slide-down', label: 'Slide Down' },
  { id: 'zoom-in', label: 'Zoom In' },
  { id: 'zoom-out', label: 'Zoom Out' },
];

const ANIMATION_TYPES = [
  { id: 'none', label: 'None' },
  { id: 'fade-in', label: 'Fade In' },
  { id: 'slide-in-left', label: 'From Left' },
  { id: 'slide-in-right', label: 'From Right' },
  { id: 'slide-in-bottom', label: 'From Bottom' },
  { id: 'zoom-in', label: 'Zoom In' },
  { id: 'bounce', label: 'Bounce' },
  { id: 'spin', label: 'Spin' },
];

const THEME_PRESETS = [
  { name: 'White', bg: '#ffffff' },
  { name: 'Dark', bg: '#1e1e2e' },
  { name: 'Navy', bg: '#0f172a' },
  { name: 'Sky', bg: '#0369a1' },
  { name: 'Forest', bg: '#166534' },
  { name: 'Wine', bg: '#9a3412' },
  { name: 'Purple', bg: '#581c87' },
  { name: 'Slate', bg: '#334155' },
];

const VIBRANT_THEMES = [
  { name: 'Coral', bg: '#ff6b6b' },
  { name: 'Sunflower', bg: '#feca57' },
  { name: 'Mint', bg: '#00d2d3' },
  { name: 'Electric', bg: '#5f27cd' },
  { name: 'Neon Pink', bg: '#ee5a9c' },
  { name: 'Amber', bg: '#ff9f43' },
  { name: 'Emerald', bg: '#10ac84' },
  { name: 'Royal', bg: '#341f97' },
  { name: 'Tangerine', bg: '#ff6348' },
  { name: 'Ocean Blue', bg: '#0abde3' },
];

const RibbonGroup = ({ label, children }) => (
  <div className="flex flex-col h-full py-1 px-2 min-w-0">
    <div className="flex-1 flex items-center gap-0.5">{children}</div>
    <div className="text-[9px] text-muted-foreground text-center leading-none mt-auto pb-0.5">{label}</div>
  </div>
);

const Div = () => <div className="w-px bg-border self-stretch my-1.5 shrink-0" />;

export const RibbonMenu = ({
  onClose, onSave, openPreview,
  addText, addShape,
  onShowImgPanel, onShowVideoPanel, onShowAudioPanel,
  deleteSelected, duplicateSelected, bringForward, sendBackward,
  undo, redo, canUndo, canRedo,
  active, isText, isShape, isImage, isMedia, isTable, hasSelection,
  updateProp,
  bgColor, changeBgColor, onBgImgUpload, clearBgImage, hasBgImage,
  setImageAsBackground,
  currentTransition, onTransitionChange, onApplyTransitionToAll,
  toggleBullets, toggleNumbering, indentList, outdentList,
  onPreviewMedia,
  addTable,
  insertTableRow, deleteTableRow, insertTableColumn, deleteTableColumn,
  updateTableCellColor, updateTableTextStyle,
}) => {
  const [activeTab, setActiveTab] = useState('home');
  const [showTableGrid, setShowTableGrid] = useState(false);
  const [tableHeaderColor, setTableHeaderColor] = useState('#5b9bd5');
  const [tableEvenColor, setTableEvenColor] = useState('#d6dce5');
  const [tableOddColor, setTableOddColor] = useState('#ffffff');
  const [tableTextColor, setTableTextColor] = useState('#1e293b');

  // Auto-switch to Table Tools tab when table is selected
  useEffect(() => {
    if (isTable && activeTab !== 'table-tools') {
      setActiveTab('table-tools');
    } else if (!isTable && activeTab === 'table-tools') {
      setActiveTab('home');
    }
  }, [isTable, activeTab]);

  const animType = active?.animationType || 'none';
  const animDuration = active?.animationDuration || 500;
  const animDelay = active?.animationDelay || 0;

  // Dynamic tabs - add Table Tools when table is selected
  const visibleTabs = isTable 
    ? [...TABS, { id: 'table-tools', label: 'Table Tools' }]
    : TABS;

  return (
    <div className="shrink-0 bg-background relative" style={{ zIndex: 100 }} data-testid="ribbon-menu">
      {/* TAB BAR */}
      <div className="flex items-center h-8 px-1.5 border-b relative bg-muted/20">
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="slide-editor-close" className="shrink-0 h-6 w-6 mr-1">
          <X className="w-3.5 h-3.5" />
        </Button>
        <span className="font-semibold text-xs whitespace-nowrap mr-3">Slide Designer</span>
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1 text-[11px] font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-background border border-b-background border-border rounded-t -mb-px relative z-10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 rounded-t'
            } ${tab.id === 'table-tools' ? 'text-blue-600' : ''}`}
            data-testid={`ribbon-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-6 shrink-0 text-[10px] gap-1" onClick={openPreview} data-testid="slide-preview-btn">
          <Eye className="w-3 h-3" /> Preview
        </Button>
        <Button size="sm" className="h-6 ml-1.5 shrink-0 text-[10px] gap-1" onClick={onSave} data-testid="slide-save-btn">
          <Save className="w-3 h-3" /> Save & Close
        </Button>
      </div>

      {/* TAB CONTENT */}
      <div className="h-[80px] border-b flex items-stretch overflow-x-auto overflow-y-visible bg-background">

        {/* HOME TAB */}
        {activeTab === 'home' && (
          <>
            <RibbonGroup label="Clipboard">
              <div className="flex flex-col gap-0.5">
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)" data-testid="tool-undo"><Undo2 className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Y)" data-testid="tool-redo"><Redo2 className="w-3.5 h-3.5" /></Button>
                </div>
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={duplicateSelected} disabled={!hasSelection} title="Duplicate (Ctrl+D)" data-testid="tool-duplicate"><Copy className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={deleteSelected} disabled={!hasSelection} title="Delete (Del)" data-testid="tool-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </RibbonGroup>
            <Div />

            {isText && (
              <>
                <RibbonGroup label="Font">
                  <div className="flex flex-col gap-0.5">
                    <select
                      value={active.fontFamily || 'Arial'}
                      onChange={(e) => updateProp('fontFamily', e.target.value)}
                      className="h-7 text-[10px] border rounded px-1 bg-background w-32"
                      data-testid="fmt-font"
                    >
                      {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <select
                      value={active.fontSize || 28}
                      onChange={(e) => updateProp('fontSize', parseInt(e.target.value))}
                      className="h-7 text-[10px] border rounded px-1 bg-background w-16"
                      data-testid="fmt-size"
                    >
                      {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </RibbonGroup>
                <Div />
                <RibbonGroup label="Format">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex gap-0.5">
                      <Button variant={active.fontWeight === 'bold' ? 'default' : 'ghost'} size="icon" className="h-7 w-7"
                        onClick={() => updateProp('fontWeight', active.fontWeight === 'bold' ? 'normal' : 'bold')} title="Bold" data-testid="fmt-bold"><Bold className="w-3.5 h-3.5" /></Button>
                      <Button variant={active.fontStyle === 'italic' ? 'default' : 'ghost'} size="icon" className="h-7 w-7"
                        onClick={() => updateProp('fontStyle', active.fontStyle === 'italic' ? 'normal' : 'italic')} title="Italic" data-testid="fmt-italic"><Italic className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="flex gap-0.5">
                      <Button variant={active.underline ? 'default' : 'ghost'} size="icon" className="h-7 w-7"
                        onClick={() => updateProp('underline', !active.underline)} title="Underline" data-testid="fmt-underline"><Underline className="w-3.5 h-3.5" /></Button>
                      <Button variant={active.linethrough ? 'default' : 'ghost'} size="icon" className="h-7 w-7"
                        onClick={() => updateProp('linethrough', !active.linethrough)} title="Strikethrough" data-testid="fmt-strikethrough"><Strikethrough className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </RibbonGroup>
                <Div />
                <RibbonGroup label="Paragraph">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex gap-0.5">
                      <Button variant={active.textAlign === 'left' ? 'default' : 'ghost'} size="icon" className="h-7 w-7"
                        onClick={() => updateProp('textAlign', 'left')} data-testid="fmt-align-left"><AlignLeft className="w-3.5 h-3.5" /></Button>
                      <Button variant={active.textAlign === 'center' ? 'default' : 'ghost'} size="icon" className="h-7 w-7"
                        onClick={() => updateProp('textAlign', 'center')} data-testid="fmt-align-center"><AlignCenter className="w-3.5 h-3.5" /></Button>
                      <Button variant={active.textAlign === 'right' ? 'default' : 'ghost'} size="icon" className="h-7 w-7"
                        onClick={() => updateProp('textAlign', 'right')} data-testid="fmt-align-right"><AlignRight className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="flex gap-1 items-center">
                      <span className="text-[9px] text-muted-foreground">LH:</span>
                      <Input type="number" min={0.5} max={3} step={0.1} value={active.lineHeight || 1.16}
                        onChange={(e) => updateProp('lineHeight', parseFloat(e.target.value) || 1.16)}
                        className="h-6 w-12 text-[10px]" data-testid="fmt-lineheight" />
                    </div>
                  </div>
                </RibbonGroup>
                <Div />
                <RibbonGroup label="Lists">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={toggleBullets} title="Bullet List" data-testid="fmt-bullets"><List className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={toggleNumbering} title="Numbered List" data-testid="fmt-numbering"><ListOrdered className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={indentList} title="Increase Indent" data-testid="fmt-indent"><IndentIncrease className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={outdentList} title="Decrease Indent" data-testid="fmt-outdent"><IndentDecrease className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </RibbonGroup>
                <Div />
                <RibbonGroup label="Colors">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground w-7">Font:</span>
                      <input type="color" value={typeof active.fill === 'string' ? active.fill : '#000000'}
                        onChange={(e) => updateProp('fill', e.target.value)}
                        className="w-6 h-6 rounded cursor-pointer border" data-testid="fmt-color" />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground w-7">BG:</span>
                      <input type="color" value={active.textBackgroundColor || '#ffff00'}
                        onChange={(e) => updateProp('textBackgroundColor', e.target.value)}
                        className="w-6 h-6 rounded cursor-pointer border" data-testid="fmt-text-bg-color" />
                      {active.textBackgroundColor && (
                        <button onClick={() => updateProp('textBackgroundColor', '')}
                          className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted" title="Clear highlight">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </RibbonGroup>
                <Div />
                <RibbonGroup label="Spacing">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground">Char:</span>
                      <Input type="number" min={-100} max={1000} step={10} value={active.charSpacing || 0}
                        onChange={(e) => updateProp('charSpacing', parseInt(e.target.value) || 0)}
                        className="h-6 w-14 text-[10px]" data-testid="fmt-spacing" />
                    </div>
                  </div>
                </RibbonGroup>
              </>
            )}

            {isShape && (
              <>
                <RibbonGroup label="Shape">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground w-7">Fill:</span>
                      <input type="color" value={typeof active.fill === 'string' ? active.fill : '#000000'}
                        onChange={(e) => updateProp('fill', e.target.value)} className="w-6 h-6 rounded cursor-pointer border" data-testid="fmt-shape-fill" />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground w-7">Line:</span>
                      <input type="color" value={active.stroke || '#000000'}
                        onChange={(e) => updateProp('stroke', e.target.value)} className="w-6 h-6 rounded cursor-pointer border" data-testid="fmt-shape-stroke" />
                      <Input type="number" min={0} max={20} value={active.strokeWidth || 0}
                        onChange={(e) => updateProp('strokeWidth', parseInt(e.target.value) || 0)} className="h-6 w-10 text-[10px]" data-testid="fmt-shape-stroke-w" />
                    </div>
                  </div>
                </RibbonGroup>
                <Div />
                <RibbonGroup label="Opacity">
                  <div className="flex flex-col gap-0.5 items-center justify-center">
                    <Slider value={[Math.round((active.opacity ?? 1) * 100)]}
                      onValueChange={([v]) => updateProp('opacity', v / 100)} min={0} max={100} step={1} className="w-24" />
                    <span className="text-[9px]">{Math.round((active.opacity ?? 1) * 100)}%</span>
                  </div>
                </RibbonGroup>
              </>
            )}

            {isImage && (
              <RibbonGroup label="Image">
                <div className="flex flex-col gap-1">
                  <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={setImageAsBackground} data-testid="fmt-set-bg">
                    <ImageDown className="w-3 h-3 mr-1" /> Set as BG
                  </Button>
                  <div className="flex items-center gap-1">
                    <Slider value={[Math.round((active.opacity ?? 1) * 100)]}
                      onValueChange={([v]) => updateProp('opacity', v / 100)} min={0} max={100} step={1} className="w-20" data-testid="fmt-img-opacity" />
                    <span className="text-[9px]">{Math.round((active.opacity ?? 1) * 100)}%</span>
                  </div>
                </div>
              </RibbonGroup>
            )}

            {isMedia && (
              <RibbonGroup label="Media">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-medium capitalize">{active.mediaType}</span>
                  <Button variant={active.mediaAutoplay ? 'default' : 'outline'} size="sm" className="h-7 text-[10px]"
                    onClick={() => updateProp('mediaAutoplay', !active.mediaAutoplay)} data-testid="fmt-autoplay">
                    <Play className="w-3 h-3 mr-1" /> {active.mediaAutoplay ? 'Autoplay ON' : 'Autoplay OFF'}
                  </Button>
                  <Button variant={active.mediaHidden ? 'default' : 'outline'} size="sm" className="h-7 text-[10px]"
                    onClick={() => updateProp('mediaHidden', !active.mediaHidden)} data-testid="fmt-media-hidden">
                    {active.mediaHidden ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                    {active.mediaHidden ? 'Hidden' : 'Visible'}
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-[10px]"
                    onClick={onPreviewMedia} data-testid="fmt-preview-media">
                    <MonitorPlay className="w-3 h-3 mr-1" /> Preview
                  </Button>
                </div>
              </RibbonGroup>
            )}

            {isTable && (
              <RibbonGroup label="Table">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-medium">
                    {active.tableRows}×{active.tableCols} Table
                  </span>
                  <span className="text-[8px] text-muted-foreground">
                    Double-click cell to edit
                  </span>
                  <span className="text-[8px] text-muted-foreground/70">
                    Drag to move table
                  </span>
                </div>
              </RibbonGroup>
            )}

            {hasSelection && (
              <>
                <Div />
                <RibbonGroup label="Arrange">
                  <div className="flex flex-col gap-0.5">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-[9px] flex items-center gap-1" onClick={bringForward} title="Bring Forward">
                      <ArrowUp className="w-3 h-3" />
                      <span>Bring Forward</span>
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-[9px] flex items-center gap-1" onClick={sendBackward} title="Send Backward">
                      <ArrowDown className="w-3 h-3" />
                      <span>Send Backward</span>
                    </Button>
                  </div>
                </RibbonGroup>
              </>
            )}

            {!hasSelection && (
              <div className="flex items-center px-4">
                <span className="text-[10px] text-muted-foreground">Select an object to format</span>
              </div>
            )}
          </>
        )}

        {/* INSERT TAB */}
        {activeTab === 'insert' && (
          <>
            <RibbonGroup label="Text">
              <Button variant="outline" className="h-14 w-14 flex flex-col gap-0.5 p-0" onClick={addText} data-testid="tool-text">
                <Type className="w-5 h-5" />
                <span className="text-[9px]">Text Box</span>
              </Button>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Table">
              <div className="relative">
                <Button variant="outline" className="h-14 w-14 flex flex-col gap-0.5 p-0"
                  onClick={() => setShowTableGrid((p) => !p)} data-testid="tool-table">
                  <Table2 className="w-5 h-5" />
                  <span className="text-[9px]">Table</span>
                </Button>
                {showTableGrid && (
                  <TableGridSelector
                    onSelect={(rows, cols) => { addTable(rows, cols); setShowTableGrid(false); }}
                    onClose={() => setShowTableGrid(false)}
                  />
                )}
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Shapes">
              <div className="flex gap-1">
                {[
                  { id: 'rect', icon: Square, label: 'Rect' },
                  { id: 'circle', icon: Circle, label: 'Circle' },
                  { id: 'triangle', icon: Triangle, label: 'Triangle' },
                  { id: 'line', icon: LineIcon, label: 'Line' },
                ].map(({ id, icon: Icon, label }) => (
                  <Button key={id} variant="outline" className="h-14 w-14 flex flex-col gap-0.5 p-0" onClick={() => addShape(id)} data-testid={`tool-${id}`}>
                    <Icon className="w-5 h-5" />
                    <span className="text-[9px]">{label}</span>
                  </Button>
                ))}
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Media">
              <div className="flex gap-1">
                <Button variant="outline" className="h-14 w-14 flex flex-col gap-0.5 p-0" onClick={onShowImgPanel} data-testid="tool-image">
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-[9px]">Image</span>
                </Button>
                <Button variant="outline" className="h-14 w-14 flex flex-col gap-0.5 p-0" onClick={onShowVideoPanel} data-testid="tool-video">
                  <Video className="w-5 h-5" />
                  <span className="text-[9px]">Video</span>
                </Button>
                <Button variant="outline" className="h-14 w-14 flex flex-col gap-0.5 p-0" onClick={onShowAudioPanel} data-testid="tool-audio">
                  <Music className="w-5 h-5" />
                  <span className="text-[9px]">Audio</span>
                </Button>
              </div>
            </RibbonGroup>
          </>
        )}

        {/* DESIGN TAB */}
        {activeTab === 'design' && (
          <>
            <RibbonGroup label="Background">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-muted-foreground">Color:</span>
                  <div className="relative group">
                    <input type="color" value={bgColor} onChange={(e) => changeBgColor(e.target.value)}
                      className="w-7 h-7 rounded cursor-pointer border" data-testid="tool-bg-color" />
                    <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[8px] text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                      Click for palette
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={onBgImgUpload} data-testid="tool-bg-image">
                    BG Image
                  </Button>
                  {hasBgImage && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive" onClick={clearBgImage}>Clear</Button>
                  )}
                </div>
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Quick Colors (click box above for full palette)">
              <div className="flex gap-1.5 items-center">
                {THEME_PRESETS.map((theme) => (
                  <button
                    key={theme.name}
                    onClick={() => changeBgColor(theme.bg)}
                    className={`w-10 h-10 rounded-md border-2 transition-all hover:scale-110 ${
                      bgColor === theme.bg ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-muted-foreground/50'
                    }`}
                    style={{ backgroundColor: theme.bg }}
                    title={theme.name}
                    data-testid={`theme-${theme.name.toLowerCase()}`}
                  />
                ))}
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Vibrant">
              <div className="flex gap-1.5 items-center">
                {VIBRANT_THEMES.map((theme) => (
                  <button
                    key={theme.name}
                    onClick={() => changeBgColor(theme.bg)}
                    className={`w-10 h-10 rounded-md border-2 transition-all hover:scale-110 ${
                      bgColor === theme.bg ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-muted-foreground/50'
                    }`}
                    style={{ backgroundColor: theme.bg }}
                    title={theme.name}
                    data-testid={`theme-${theme.name.toLowerCase().replace(/\s/g, '-')}`}
                  />
                ))}
              </div>
            </RibbonGroup>
          </>
        )}

        {/* TRANSITION TAB */}
        {activeTab === 'transition' && (
          <>
            <RibbonGroup label="Transition Effect">
              <div className="flex gap-1">
                {TRANSITION_TYPES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => onTransitionChange({ ...currentTransition, type: t.id })}
                    className={`px-2 h-11 rounded-md border text-[10px] transition-all min-w-[54px] ${
                      currentTransition.type === t.id
                        ? 'border-primary bg-primary/10 text-primary font-semibold'
                        : 'border-border hover:border-muted-foreground/50 hover:bg-muted/30'
                    }`}
                    data-testid={`transition-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Timing">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1">
                  <span className="text-[9px] text-muted-foreground whitespace-nowrap">Duration:</span>
                  <Input type="number" min={100} max={3000} step={100}
                    value={currentTransition.duration || 500}
                    onChange={(e) => onTransitionChange({ ...currentTransition, duration: parseInt(e.target.value) || 500 })}
                    className="h-6 w-16 text-[10px]" data-testid="transition-duration" />
                  <span className="text-[9px] text-muted-foreground">ms</span>
                </div>
                <Button variant="outline" size="sm" className="h-6 text-[10px]"
                  onClick={() => onApplyTransitionToAll(currentTransition)} data-testid="transition-apply-all">
                  Apply to All Slides
                </Button>
              </div>
            </RibbonGroup>
          </>
        )}

        {/* ANIMATION TAB */}
        {activeTab === 'animation' && (
          <>
            {hasSelection ? (
              <>
                <RibbonGroup label="Entrance Effect">
                  <div className="flex gap-1">
                    {ANIMATION_TYPES.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => updateProp('animationType', a.id)}
                        className={`px-2 h-11 rounded-md border text-[10px] transition-all min-w-[52px] ${
                          animType === a.id
                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                            : 'border-border hover:border-muted-foreground/50 hover:bg-muted/30'
                        }`}
                        data-testid={`animation-${a.id}`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                </RibbonGroup>
                <Div />
                <RibbonGroup label="Timing">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground">Duration:</span>
                      <Input type="number" min={100} max={3000} step={100}
                        value={animDuration}
                        onChange={(e) => updateProp('animationDuration', parseInt(e.target.value) || 500)}
                        className="h-6 w-14 text-[10px]" data-testid="animation-duration" />
                      <span className="text-[9px]">ms</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground">Delay:</span>
                      <Input type="number" min={0} max={5000} step={100}
                        value={animDelay}
                        onChange={(e) => updateProp('animationDelay', parseInt(e.target.value) || 0)}
                        className="h-6 w-14 text-[10px]" data-testid="animation-delay" />
                      <span className="text-[9px]">ms</span>
                    </div>
                  </div>
                </RibbonGroup>
              </>
            ) : (
              <div className="flex items-center px-4">
                <span className="text-[10px] text-muted-foreground">Select an object to add entrance animation</span>
              </div>
            )}
          </>
        )}

        {/* TABLE TOOLS TAB */}
        {activeTab === 'table-tools' && isTable && (
          <>
            <RibbonGroup label="Table Info">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium">
                  {active.tableRows}×{active.tableCols} Table
                </span>
                <span className="text-[8px] text-muted-foreground">
                  Double-click cell to edit
                </span>
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Rows">
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex flex-col items-center gap-0.5"
                  onClick={() => insertTableRow('above')} title="Insert Row Above" data-testid="table-insert-row-above">
                  <ArrowUp className="w-3 h-3" />
                  <span>Above</span>
                </Button>
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex flex-col items-center gap-0.5"
                  onClick={() => insertTableRow('below')} title="Insert Row Below" data-testid="table-insert-row-below">
                  <ArrowDown className="w-3 h-3" />
                  <span>Below</span>
                </Button>
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex flex-col items-center gap-0.5 text-destructive hover:text-destructive"
                  onClick={deleteTableRow} title="Delete Row" data-testid="table-delete-row">
                  <Trash className="w-3 h-3" />
                  <span>Delete</span>
                </Button>
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Columns">
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex flex-col items-center gap-0.5"
                  onClick={() => insertTableColumn('left')} title="Insert Column Left" data-testid="table-insert-col-left">
                  <ArrowUp className="w-3 h-3 -rotate-90" />
                  <span>Left</span>
                </Button>
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex flex-col items-center gap-0.5"
                  onClick={() => insertTableColumn('right')} title="Insert Column Right" data-testid="table-insert-col-right">
                  <ArrowDown className="w-3 h-3 -rotate-90" />
                  <span>Right</span>
                </Button>
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex flex-col items-center gap-0.5 text-destructive hover:text-destructive"
                  onClick={deleteTableColumn} title="Delete Column" data-testid="table-delete-col">
                  <Trash className="w-3 h-3" />
                  <span>Delete</span>
                </Button>
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Cell Colors">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-[8px] text-muted-foreground w-12">Header:</span>
                  <input 
                    type="color" 
                    value={tableHeaderColor}
                    onChange={(e) => {
                      setTableHeaderColor(e.target.value);
                      updateTableCellColor(e.target.value, 'header');
                    }}
                    className="w-6 h-6 rounded cursor-pointer border"
                    title="Header row color"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[8px] text-muted-foreground w-12">Even:</span>
                  <input 
                    type="color" 
                    value={tableEvenColor}
                    onChange={(e) => {
                      setTableEvenColor(e.target.value);
                      updateTableCellColor(e.target.value, 'evenRows');
                    }}
                    className="w-6 h-6 rounded cursor-pointer border"
                    title="Even rows color"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[8px] text-muted-foreground w-12">Odd:</span>
                  <input 
                    type="color" 
                    value={tableOddColor}
                    onChange={(e) => {
                      setTableOddColor(e.target.value);
                      updateTableCellColor(e.target.value, 'oddRows');
                    }}
                    className="w-6 h-6 rounded cursor-pointer border"
                    title="Odd rows color"
                  />
                </div>
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Text Style">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-[8px] text-muted-foreground w-10">Color:</span>
                  <input 
                    type="color" 
                    value={tableTextColor}
                    onChange={(e) => {
                      setTableTextColor(e.target.value);
                      updateTableTextStyle('fill', e.target.value);
                    }}
                    className="w-6 h-6 rounded cursor-pointer border"
                    title="Text color"
                  />
                </div>
                <div className="flex gap-0.5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => updateTableTextStyle('fontWeight', 'bold')}
                    title="Bold"
                  >
                    <Bold className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => updateTableTextStyle('fontStyle', 'italic')}
                    title="Italic"
                  >
                    <Italic className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => updateTableTextStyle('fontWeight', 'normal')}
                    title="Normal"
                  >
                    <Type className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </RibbonGroup>
            <Div />
            <RibbonGroup label="Actions">
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex items-center gap-1"
                  onClick={bringForward} title="Bring Forward">
                  <ArrowUp className="w-3 h-3" />
                  Forward
                </Button>
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex items-center gap-1"
                  onClick={sendBackward} title="Send Backward">
                  <ArrowDown className="w-3 h-3" />
                  Backward
                </Button>
                <Button variant="outline" size="sm" className="h-8 px-2 text-[9px] flex items-center gap-1 text-destructive hover:text-destructive"
                  onClick={deleteSelected} title="Delete Table" data-testid="table-delete">
                  <Trash2 className="w-3 h-3" />
                  Delete
                </Button>
              </div>
            </RibbonGroup>
          </>
        )}
      </div>
    </div>
  );
};
